import {
  OAUTH_PROVIDERS,
  OAUTH2_PROVIDER,
  PREBUILT_OAUTH_PROVIDERS,
} from "./oauth-providers";
import { TOOLKIT_CATALOGUE, TOOLKITS } from "./toolkits";
import type { OAuthCatalogue, OAuthProviderIdType, Toolkit } from "./types";

/**
 * OAUTH PROVIDERS
 */

/**
 * Find a provider whose `provider_id` OR internal `id` matches, case-insensitively.
 * Tool metadata declares its auth key inconsistently — sometimes the `provider_id`
 * (`reddit`), sometimes the `arcade-`-prefixed `id` (`arcade-airtable`), and the
 * casing does not always match the catalog. Returns undefined when nothing
 * matches so callers can fall back to other branding (e.g. a toolkit icon).
 *
 * Iterating the provider list (rather than indexing the catalog by key) also
 * makes inherited keys like `__proto__` or `constructor` simply not match,
 * instead of resolving to a built-in and crashing the caller.
 */
export const findOAuthProvider = (
  idOrProviderId: string | undefined
): OAuthCatalogue | undefined => {
  // Provider payloads reach us unvalidated, so a missing/blank key must resolve
  // to no match (and thus the generic fallback) rather than crash on toLowerCase.
  if (!idOrProviderId) {
    return undefined;
  }
  const normalized = idOrProviderId.toLowerCase();
  return OAUTH_PROVIDERS.find(
    (provider) =>
      provider.provider_id.toLowerCase() === normalized ||
      provider.id.toLowerCase() === normalized
  );
};

/**
 * Find a prebuilt provider by its provider ID or internal ID, case-insensitively.
 */
export const findPrebuiltProviderByProviderId = (
  providerId: OAuthProviderIdType | string | undefined
): OAuthCatalogue | undefined => {
  if (!providerId) {
    return undefined;
  }
  const normalized = providerId.toLowerCase();
  return PREBUILT_OAUTH_PROVIDERS.find(
    (item) =>
      item.provider_id.toLowerCase() === normalized ||
      item.id.toLowerCase() === normalized
  );
};

/**
 * Find a prebuilt provider by its ID (not provider ID), case-insensitively.
 */
export const findPrebuiltProviderById = (
  id: string | undefined
): OAuthCatalogue | undefined => {
  if (!id) {
    return undefined;
  }
  const normalized = id.toLowerCase();
  return PREBUILT_OAUTH_PROVIDERS.find(
    (item) => item.id.toLowerCase() === normalized
  );
};

/**
 * Check if a provider ID (or internal ID) corresponds to a prebuilt provider.
 */
export const isPrebuiltProvider = (providerId: string | undefined): boolean =>
  findPrebuiltProviderByProviderId(providerId) !== undefined;

/**
 * Resolve a provider `provider_id` or internal `id` to its catalog descriptor
 * across all providers, falling back to the generic OAuth 2.0 descriptor for
 * custom or unknown ones.
 */
export const resolveOAuthProvider = (
  providerId: string | undefined
): OAuthCatalogue => findOAuthProvider(providerId) ?? OAUTH2_PROVIDER;

/**
 * TOOLKITS
 */

/**
 * Check if a toolkit name exists in the catalog
 */
export const isKnownToolkit = (toolkitName: string | undefined): boolean => {
  if (!toolkitName) {
    return false;
  }
  const normalized = toolkitName.toLowerCase();
  return Object.keys(TOOLKIT_CATALOGUE).some(
    (key) => key.toLowerCase() === normalized
  );
};

/**
 * Find a toolkit by its name or ID, with case-insensitive matching
 * @returns Toolkit object, or undefined if not found
 */
export const findToolkitByName = (
  toolkitName: string | undefined
): Toolkit | undefined => {
  if (!toolkitName) {
    return undefined;
  }
  const normalizedName = toolkitName.toLowerCase();
  const matches = TOOLKITS.filter(
    (entry) =>
      entry.label.toLowerCase() === normalizedName ||
      entry.id.toLowerCase() === normalizedName
  );
  if (matches.length === 0) {
    return undefined;
  }
  // Prefer visible entries when label/id matches more than one row (e.g. legacy
  // "Sharepoint" vs "MicrosoftSharePoint" both used "Microsoft SharePoint").
  return matches.find((entry) => !entry.isHidden) ?? matches[0];
};
